import React, { memo } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './MobileRows.styles';

/**
 * Mobile fallback for <DataTable />: renders each row as a stacked "composite" card instead of a
 * table row. Driven by the SAME `columns`/`data`/`rowKey` as the table, so a consumer opts a table
 * into the mobile layout simply by passing `<MobileRows ... />` to DataTable's `mobile` slot.
 *
 *  - Columns with a `label` become a "label : value" line. Pass `mobileBlock` on the column to
 *    stack the value under the label full-width instead (for wide content like a media strip).
 *  - Columns without a `label` (the selection checkbox, the row actions) are grouped into a header
 *    strip at the top of the card — the first sits on the left, the rest trail on the right.
 *
 * Use the plain column config here (not sortable-header-wrapped columns) so `label` is the field
 * name text. Sorting stays a desktop affordance, matching the legacy mobile tables.
 */
const MobileRows = ({ columns, data, rowKey, rowProps, emptyText }) => {
  if (!data.length) return <Styled.Empty>{emptyText}</Styled.Empty>;

  const actionCols = columns.filter((col) => !col.label);
  const fieldCols = columns.filter((col) => col.label);

  return (
    // `data-pdf-*`: Print / Save to PDF reads the cards as a table (utils/custom/tablePdf).
    <Styled.List data-pdf-cards="">
      {/* Same rowKey contract as the table (row, index) -- see the note in ./index.js. */}
      {data.map((row, index) => (
        <Styled.Card key={rowKey(row, index)} data-pdf-card="" {...(rowProps ? rowProps(row) : {})}>
          {actionCols.length > 0 && (
            <Styled.ActionBar data-pdf-ignore="">
              {actionCols.map((col) => (
                <Styled.Action key={col.key}>{col.render(row)}</Styled.Action>
              ))}
            </Styled.ActionBar>
          )}
          {fieldCols.map((col) => (
            <Styled.Field key={col.key} block={col.mobileBlock} data-pdf-field="">
              <Styled.Label data-pdf-label="">{col.label}</Styled.Label>
              <Styled.Value block={col.mobileBlock} data-pdf-value="">
                {col.render(row)}
              </Styled.Value>
            </Styled.Field>
          ))}
        </Styled.Card>
      ))}
    </Styled.List>
  );
};

MobileRows.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      mobileBlock: PropTypes.bool,
      render: PropTypes.func.isRequired,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  rowKey: PropTypes.func.isRequired,
  rowProps: PropTypes.func,
  emptyText: PropTypes.node,
};

MobileRows.defaultProps = {
  data: [],
  rowProps: undefined,
  emptyText: 'No records found.',
};

export default memo(MobileRows);
